/* TODO:
  - Remove hover logic on mobile/touch devices to increase efficiency
*/
class LabImage extends HTMLElement {
  constructor() {
    super();

    this.shadow = this.attachShadow({mode: 'open'});
    const style = document.createElement('style');
    style.textContent = `
      :host div {
        position: relative;
      }
      :host div::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: var(--neutral-100);
      }
      :host div picture {
        display: block;
        max-width: 100%;
        height: 100%;
        width: 100%;
      }
      :host div img {
        position: relative;
        width: 100%;
        height: 100%;
        object-fit: cover;
        z-index: 1;
      }
      :host div.pgx-transparent-image::after {
        background: transparent;
      } 
    `;

    this.shadowRoot.appendChild(style);
  }

  set source(value) {
    this.setAttribute('source', value);
  }

  get source() {
    return this.getAttribute('source');
  }

  static get observedAttributes() {
    return ['source', 'data-source-mobile'];
  }

  attributeChangedCallback(_, oldValue, newValue) {
    if (!oldValue || oldValue === newValue) return;

    this.updatePictureContent();
  }

  connectedCallback() {
    // Unbound attributes are those managed by Vue's :bind syntax.
    // Avoid rendering until these attributes are correctly bound by Vue.
    // When bound, this will trigger a change callback and the render method will run again.
    const hasUnboundAttributes = Array.from(this.attributes).some((attribute) =>
      attribute.name.startsWith(':')
    );
    if (hasUnboundAttributes) return;

    this.mediaQueryDesktop = window.matchMedia('(min-width: 768px)');
    this.updateAspectRatio = this.updateAspectRatio.bind(this);
    this.mediaQueryDesktop.addEventListener('change', this.updateAspectRatio);

    this.render();

    if (this.source && this.sourceHover) {
      this.addEventListener('mouseenter', this.handleImageChangeOnMouseEnter);
      this.addEventListener('mouseleave', this.restoreOriginalSourceOnMouseLeave);
    }
  }

  disconnectedCallback() {
    if (this.mediaQueryDesktop) {
      this.mediaQueryDesktop.removeEventListener('change', this.updateAspectRatio);
    }

    this.removeEventListener('mouseenter', this.handleImageChangeOnMouseEnter);
    this.removeEventListener('mouseleave', this.restoreOriginalSourceOnMouseLeave);
  }

  updateAspectRatio() {
    if (!this.wrapper || !this.mediaQueryDesktop) return;

    if (this.mediaQueryDesktop.matches) {
      if (!this.baseWidth || !this.baseHeight) return;

      this.wrapper.style.aspectRatio = `${this.baseWidth} / ${this.baseHeight}`;
      return;
    }

    if (!this.baseWidthMobile || !this.baseHeightMobile) return;

    this.wrapper.style.aspectRatio = `${this.baseWidthMobile} / ${this.baseHeightMobile}`;
  }

  handleImageChangeOnMouseEnter() {
    if (this.isHovering) return; // Prevent multiple executions
    this.isHovering = true;

    // Keep a reference to the original source to revert on mouse leave
    if (!this.originalSource) {
      this.originalSource = this.source;
    }
    this.source = this.sourceHover;
  }

  restoreOriginalSourceOnMouseLeave() {
    this.isHovering = false;
    this.source = this.originalSource;
  }

  generatePictureContent() {
    const pictureFragment = document.createDocumentFragment();

    this.source = this.getAttribute('source');
    if (!this.source) return pictureFragment;

    this.sourceMobile = this.getAttribute('data-source-mobile') || this.source;

    const BASE_SCREEN_WIDTH = 1280;
    const BASE_SCREEN_WIDTH_MOBILE = 375;
    const aspectRatio = this.baseHeight / this.baseWidth;
    const aspectRatioMobile = this.baseHeightMobile / this.baseWidthMobile;

    const generateImageUrl = (src, width, height) =>
      `${src}?crop=center&width=${width}&height=${height}`;

    const breakpointsMobile = [375, 550, 768];
    const breakpoints = [1024, 1280, 1440, 1600, 1920];

    breakpointsMobile.forEach((breakpoint) => {
      const width = Math.round((breakpoint * this.baseWidthMobile) / BASE_SCREEN_WIDTH_MOBILE);
      const height = Math.round(width * aspectRatioMobile);
      const sourceEl = document.createElement('source');
      sourceEl.type = 'image/webp';
      sourceEl.media = `(max-width: ${breakpoint}px)`;
      sourceEl.srcset = `${generateImageUrl(this.sourceMobile, width, height)} 1x, ${generateImageUrl(
        this.sourceMobile,
        Math.round(width * 1.25),
        Math.round(height * 1.25)
      )} 2x`;
      pictureFragment.appendChild(sourceEl);
    });

    breakpoints.forEach((breakpoint, index) => {
      const width = Math.round((breakpoint * this.baseWidth) / BASE_SCREEN_WIDTH);
      const height = Math.round(width * aspectRatio);
      const sourceEl = document.createElement('source');
      sourceEl.type = 'image/webp';
      sourceEl.media = index !== breakpoints.length - 1 ? `(max-width: ${breakpoint}px)` : '';
      sourceEl.srcset = `${generateImageUrl(this.source, width, height)} 1x, ${generateImageUrl(
        this.source,
        Math.round(width * 1.25),
        Math.round(height * 1.25)
      )} 2x`;
      pictureFragment.appendChild(sourceEl);
    });

    const img = document.createElement('img');
    img.src = generateImageUrl(this.sourceMobile, this.baseWidthMobile, this.baseHeightMobile);
    img.alt = this.getAttribute('alt') || '';
    img.width = 'auto';
    img.height = 'auto';
    img.loading = this.getAttribute('loading') || 'lazy';
    pictureFragment.appendChild(img);

    return pictureFragment;
  }

  updatePictureContent() {
    if (!this.picture) return;

    const newPictureContent = this.generatePictureContent();
    this.picture.innerHTML = '';
    this.picture.appendChild(newPictureContent);
  }

  render() {
    this.wrapper = document.createElement('div');

    this.sourceHover = this.getAttribute('data-source-hover');
    this.baseWidth = parseFloat(this.getAttribute('data-base-width'));
    this.baseHeight = parseFloat(this.getAttribute('data-base-height'));
    this.baseWidthMobile = parseFloat(this.getAttribute('data-base-width-mobile'));
    this.baseHeightMobile = parseFloat(this.getAttribute('data-base-height-mobile'));
    this.isTransparent = this.getAttribute('data-is-transparent');
    if (this.isTransparent === 'true') {
      this.wrapper.classList.add('pgx-transparent-image');
    }

    this.updateAspectRatio(); // Set the ratio initially

    this.picture = document.createElement('picture');
    this.picture.appendChild(this.generatePictureContent());

    this.wrapper.appendChild(this.picture);
    this.shadowRoot.appendChild(this.wrapper);
  }
}

customElements.define('lab-image', LabImage);
